import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, TrendingUp, TrendingDown, MapPin, Users, ArrowRight, Star, Filter, Search, Navigation, Building2, ChevronDown } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
// Radar chart data
const radarData = [{
  category: 'Preis-Leistung',
  BMW: 78,
  Mercedes: 72,
  Audi: 80
}, {
  category: 'Servicequalität',
  BMW: 88,
  Mercedes: 85,
  Audi: 82
}, {
  category: 'Beratung',
  BMW: 82,
  Mercedes: 79,
  Audi: 78
}, {
  category: 'Sauberkeit',
  BMW: 85,
  Mercedes: 88,
  Audi: 84
}, {
  category: 'Fahrzeugqualität',
  BMW: 90,
  Mercedes: 92,
  Audi: 88
}, {
  category: 'Erreichbarkeit',
  BMW: 75,
  Mercedes: 73,
  Audi: 77
}];
// Win/Loss data
const competitorAnalysis = [{
  location: 'Hamburg-Hafencity',
  edeka: 'BMW Niederlassung Hamburg',
  competitor: 'Mercedes-Benz Hamburg',
  competitorType: 'Mercedes',
  distance: '0.8 km',
  status: 'winning',
  edekaRating: 4.6,
  competitorRating: 4.2,
  insight: 'Bessere Beratung und Servicequalität',
  customerFlow: '+12%'
}, {
  location: 'Berlin-Kreuzberg',
  edeka: 'Autohaus Welzel Berlin',
  competitor: 'Audi Zentrum Berlin',
  competitorType: 'Audi',
  distance: '0.3 km',
  status: 'losing',
  edekaRating: 3.8,
  competitorRating: 4.1,
  insight: 'Wartezeiten im Service problematisch',
  customerFlow: '-8%'
}, {
  location: 'München-Schwabing',
  edeka: 'BMW Niederlassung München',
  competitor: 'Mercedes-Benz München',
  competitorType: 'Mercedes',
  distance: '1.2 km',
  status: 'winning',
  edekaRating: 4.8,
  competitorRating: 4.3,
  insight: 'Premium-Service und Beratung überzeugen',
  customerFlow: '+18%'
}, {
  location: 'Köln-Ehrenfeld',
  edeka: 'Autohaus Fleischhauer Köln',
  competitor: 'Audi Köln',
  competitorType: 'Audi',
  distance: '0.5 km',
  status: 'losing',
  edekaRating: 3.2,
  competitorRating: 4.0,
  insight: 'Servicequalität und Freundlichkeit verbessern',
  customerFlow: '-15%'
}, {
  location: 'Frankfurt-Sachsenhausen',
  edeka: 'Autohaus Wahl Frankfurt',
  competitor: 'Mercedes-Benz Frankfurt',
  competitorType: 'Mercedes',
  distance: '0.6 km',
  status: 'neutral',
  edekaRating: 4.2,
  competitorRating: 4.2,
  insight: 'Gleichstand – Differenzierung durch iDrive-Beratung möglich',
  customerFlow: '0%'
}, {
  location: 'Düsseldorf-Bilk',
  edeka: 'BMW Niederlassung Düsseldorf',
  competitor: 'Tesla Düsseldorf',
  competitorType: 'Tesla',
  distance: '0.4 km',
  status: 'winning',
  edekaRating: 4.3,
  competitorRating: 3.9,
  insight: 'Fahrzeugqualität und Beratung als Stärken',
  customerFlow: '+7%'
}];
const competitorColors: Record<string, string> = {
  Mercedes: '#222222',
  Audi: '#BB0A21',
  Tesla: '#CC0000'
};
// Regional rating data per competitor
const regionalRatings = [{
  region: 'Deutschland',
  BMW: 4.3,
  Mercedes: 4.0,
  Audi: 3.8,
  Tesla: 3.6,
  markets: 82
}, {
  region: 'USA',
  BMW: 4.1,
  Mercedes: 4.0,
  Audi: 3.8,
  Tesla: 3.9,
  markets: 64
}, {
  region: 'China',
  BMW: 4.0,
  Mercedes: 3.9,
  Audi: 4.1,
  Tesla: 3.8,
  markets: 118
}, {
  region: 'Großbritannien',
  BMW: 4.3,
  Mercedes: 4.1,
  Audi: 3.7,
  Tesla: 3.5,
  markets: 38
}, {
  region: 'Frankreich',
  BMW: 4.1,
  Mercedes: 3.9,
  Audi: 3.6,
  Tesla: 3.4,
  markets: 29
}, {
  region: 'Australien',
  BMW: 4.4,
  Mercedes: 4.2,
  Audi: 3.8,
  Tesla: 4.0,
  markets: 22
}, {
  region: 'Japan',
  BMW: 3.9,
  Mercedes: 4.0,
  Audi: 3.7,
  Tesla: 4.2,
  markets: 31
}];
// Radius-based mock data
const radiusData: Record<string, {
  name: string;
  type: string;
  rating: number;
  distance: string;
}[]> = {
  'Hamburg, Jungfernstieg': [{
    name: 'BMW Niederlassung Hamburg',
    type: 'BMW',
    rating: 4.3,
    distance: '0.6 km'
  }, {
    name: 'Mercedes-Benz Hamburg City',
    type: 'Mercedes',
    rating: 3.9,
    distance: '0.2 km'
  }, {
    name: 'Audi Zentrum Hamburg',
    type: 'Audi',
    rating: 3.7,
    distance: '0.8 km'
  }, {
    name: 'Tesla Hamburg-Mitte',
    type: 'Tesla',
    rating: 3.5,
    distance: '1.1 km'
  }, {
    name: 'Autohaus Märtin Hamburg',
    type: 'BMW',
    rating: 4.1,
    distance: '1.4 km'
  }],
  'Berlin, Alexanderplatz': [{
    name: 'BMW Niederlassung Berlin',
    type: 'BMW',
    rating: 3.7,
    distance: '0.3 km'
  }, {
    name: 'Mercedes-Benz Berlin-Mitte',
    type: 'Mercedes',
    rating: 4.1,
    distance: '0.5 km'
  }, {
    name: 'Audi Zentrum Berlin',
    type: 'Audi',
    rating: 4.0,
    distance: '0.7 km'
  }, {
    name: 'Tesla Berlin-Mitte',
    type: 'Tesla',
    rating: 3.8,
    distance: '1.2 km'
  }],
  'München, Marienplatz': [{
    name: 'BMW Niederlassung München',
    type: 'BMW',
    rating: 4.6,
    distance: '0.9 km'
  }, {
    name: 'Mercedes-Benz München',
    type: 'Mercedes',
    rating: 4.2,
    distance: '0.4 km'
  }, {
    name: 'Audi München-Zentrum',
    type: 'Audi',
    rating: 3.8,
    distance: '1.3 km'
  }, {
    name: 'Autohaus Schönaich München',
    type: 'BMW',
    rating: 4.0,
    distance: '1.8 km'
  }]
};
const regions = ['Alle Länder', 'Deutschland', 'USA', 'China', 'Großbritannien', 'Frankreich', 'Australien', 'Japan'];
const radiusOptions = ['1 km', '2 km', '5 km', '10 km'];
const competitors = ['BMW', 'Mercedes', 'Audi', 'Tesla'];
const competitorBarColors: Record<string, string> = {
  BMW: '#1C69D4',
  Mercedes: '#222222',
  Audi: '#BB0A21',
  Tesla: '#CC0000'
};
export function CompetitorRadarPage() {
  const [filterMode, setFilterMode] = useState<'region' | 'radius'>('region');
  const [selectedRegion, setSelectedRegion] = useState('Alle Länder');
  const [addressInput, setAddressInput] = useState('');
  const [selectedRadius, setSelectedRadius] = useState('5 km');
  const [addressSearched, setAddressSearched] = useState('Hamburg, Jungfernstieg');
  const [hasSearched, setHasSearched] = useState(true);
  const winningCount = competitorAnalysis.filter(c => c.status === 'winning').length;
  const losingCount = competitorAnalysis.filter(c => c.status === 'losing').length;
  // Filter regional data
  const filteredRegionalData = selectedRegion === 'Alle Länder' ? regionalRatings : regionalRatings.filter(r => r.region === selectedRegion);
  // Build bar chart data for region view
  const regionBarData = filteredRegionalData.map(r => ({
    region: r.region,
    BMW: r.BMW,
    Mercedes: r.Mercedes,
    Audi: r.Audi,
    Tesla: r.Tesla
  }));
  // Radius view: aggregate by type
  const radiusStores = radiusData[addressSearched] || radiusData['Hamburg, Jungfernstieg'];
  const radiusAggregated = competitors.map(type => {
    const stores = radiusStores.filter(s => s.type === type);
    const avg = stores.length > 0 ? stores.reduce((acc, s) => acc + s.rating, 0) / stores.length : null;
    return {
      name: type,
      rating: avg,
      count: stores.length
    };
  }).filter(d => d.rating !== null);
  const handleSearch = () => {
    const key = Object.keys(radiusData).find(k => k.toLowerCase().includes(addressInput.toLowerCase().split(',')[0].toLowerCase())) || 'Hamburg, Jungfernstieg';
    setAddressSearched(key);
    setHasSearched(true);
  };
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-354">
      {/* Header KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-355">
        <KPICard title="Marktposition" value="#2" change="stabil" trend="up" icon={<Target size={18} data-id="element-357" />} subtext="weltweit" data-id="element-356" />
        <KPICard title="Gewonnene Standorte" value={winningCount} change={`von ${competitorAnalysis.length}`} trend="up" icon={<TrendingUp size={18} data-id="element-359" />} subtext="im 5km Radius" data-id="element-358" />
        <KPICard title="Verlorene Standorte" value={losingCount} change="Handlungsbedarf" trend="down" icon={<TrendingDown size={18} data-id="element-361" />} subtext="an Konkurrenz" data-id="element-360" />
        <KPICard title="Ø Rating-Vorsprung" value="+0.3" change="vs. Wettbewerb" trend="up" icon={<Users size={18} data-id="element-363" />} subtext="Sterne" data-id="element-362" />
      </div>

      {/* Rating Comparison Card */}
      <Card data-id="element-364">
        <div className="p-6" data-id="element-365">
          <div className="flex items-start justify-between mb-5 flex-wrap gap-4" data-id="element-366">
            <div data-id="element-367">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-368">
                BMW vs. Mitbewerber – Bewertungsvergleich
              </h3>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-369">
                Durchschnittliche Google-Bewertungen im direkten Vergleich
              </p>
            </div>
            <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1" data-id="element-370">
              <button onClick={() => setFilterMode('region')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterMode === 'region' ? 'bg-white text-[#1C69D4] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-371">
                <Building2 size={14} data-id="element-372" />
                Region
              </button>
              <button onClick={() => setFilterMode('radius')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filterMode === 'radius' ? 'bg-white text-[#1C69D4] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-373">
                <Navigation size={14} data-id="element-374" />
                Umkreis
              </button>
            </div>
          </div>

          <AnimatePresence mode="wait" data-id="element-375">
            {filterMode === 'region' ? <motion.div key="region" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.18
          }} data-id="element-376">
                <div className="flex items-center gap-3 mb-5 flex-wrap" data-id="element-377">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-600" data-id="element-378">
                    <Filter size={14} data-id="element-379" />
                    <span data-id="element-380">Land:</span>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-381">
                    {regions.map(region => <button key={region} onClick={() => setSelectedRegion(region)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedRegion === region ? 'bg-[#1C69D4] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-382">
                        {region}
                      </button>)}
                  </div>
                </div>
                <div className="h-64" data-id="element-383">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-384">
                    <BarChart data={regionBarData} margin={{
                  top: 5,
                  right: 20,
                  left: 0,
                  bottom: 5
                }} barCategoryGap="25%" barGap={3} data-id="element-385">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-386" />
                      <XAxis dataKey="region" axisLine={false} tickLine={false} tick={{
                    fontSize: 11,
                    fill: '#6b7280'
                  }} data-id="element-387" />
                      <YAxis domain={[3, 5]} axisLine={false} tickLine={false} tick={{
                    fontSize: 11,
                    fill: '#9ca3af'
                  }} tickFormatter={v => v.toFixed(1) + ' ★'} data-id="element-388" />
                      <Tooltip contentStyle={{
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    fontSize: '12px'
                  }} formatter={(value: number, name: string) => [value.toFixed(1) + ' ★', name]} data-id="element-389" />
                      {competitors.map(comp => <Bar key={comp} dataKey={comp} name={comp} fill={competitorBarColors[comp]} radius={[3, 3, 0, 0]} fillOpacity={comp === 'BMW' ? 1 : 0.55} data-id="element-390" />)}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-center gap-6 mt-3 flex-wrap" data-id="element-391">
                  {competitors.map(comp => <div key={comp} className="flex items-center gap-1.5" data-id="element-392">
                      <div className="w-3 h-3 rounded-sm" style={{
                  backgroundColor: competitorBarColors[comp],
                  opacity: comp === 'BMW' ? 1 : 0.55
                }} data-id="element-393" />
                      <span className={`text-xs ${comp === 'BMW' ? 'font-semibold text-gray-800' : 'text-gray-500'}`} data-id="element-394">
                        {comp}
                      </span>
                    </div>)}
                </div>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-395">
                  {filteredRegionalData.slice(0, 4).map(r => {
                const isLeading = r.BMW >= Math.max(r.Mercedes, r.Audi, r.Tesla);
                return <div key={r.region} className={`p-3 rounded-lg border ${isLeading ? 'border-green-200 bg-green-50' : 'border-red-100 bg-red-50/50'}`} data-id="element-396">
                        <div className="text-xs font-medium text-gray-600 mb-1" data-id="element-397">
                          {r.region}
                        </div>
                        <div className="flex items-center gap-1.5" data-id="element-398">
                          <span className="text-lg font-bold text-[#1C69D4]" data-id="element-399">
                            {r.BMW.toFixed(1)}
                          </span>
                          <span className="text-xs text-gray-400" data-id="element-400">★</span>
                          {isLeading ? <Badge variant="success" className="text-[10px] py-0" data-id="element-401">
                              Führend
                            </Badge> : <Badge variant="warning" className="text-[10px] py-0" data-id="element-402">
                              Aufholen
                            </Badge>}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5" data-id="element-403">
                          {r.markets} Autohäuser
                        </div>
                      </div>;
              })}
                </div>
              </motion.div> : <motion.div key="radius" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.18
          }} data-id="element-404">
                <div className="flex gap-3 mb-5 flex-wrap" data-id="element-405">
                  <div className="flex-1 min-w-48 relative" data-id="element-406">
                    <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-407" />
                    <input type="text" placeholder="Adresse eingeben, z.B. Hamburg, Jungfernstieg" value={addressInput} onChange={e => setAddressInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C69D4]/20 focus:border-[#1C69D4]" data-id="element-408" />
                  </div>
                  <div className="flex items-center gap-2" data-id="element-409">
                    <span className="text-sm text-gray-500 whitespace-nowrap" data-id="element-410">
                      Radius:
                    </span>
                    <div className="flex gap-1" data-id="element-411">
                      {radiusOptions.map(r => <button key={r} onClick={() => setSelectedRadius(r)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${selectedRadius === r ? 'bg-[#1C69D4] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-412">
                          {r}
                        </button>)}
                    </div>
                  </div>
                  <button onClick={handleSearch} className="flex items-center gap-2 px-4 py-2.5 bg-[#1C69D4] text-white rounded-lg text-sm font-medium hover:bg-[#0653B6] transition-colors" data-id="element-413">
                    <Search size={14} data-id="element-414" />
                    Suchen
                  </button>
                </div>
                {hasSearched && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2
            }} data-id="element-415">
                    <div className="text-xs text-gray-500 mb-4 flex items-center gap-1.5" data-id="element-416">
                      <Navigation size={12} data-id="element-417" />
                      <span data-id="element-418">
                        Ergebnisse für{' '}
                        <strong className="text-gray-700" data-id="element-419">
                          {addressSearched}
                        </strong>{' '}
                        im Umkreis von{' '}
                        <strong className="text-gray-700" data-id="element-420">
                          {selectedRadius}
                        </strong>{' '}
                        — {radiusStores.length} Autohäuser gefunden
                      </span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-421">
                      <div data-id="element-422">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-423">
                          Ø Bewertung nach Marke
                        </div>
                        <div className="space-y-3" data-id="element-424">
                          {radiusAggregated.sort((a, b) => (b.rating || 0) - (a.rating || 0)).map((item, i) => <div key={item.name} className="flex items-center gap-3" data-id="element-425">
                                <div className="w-2 h-2 rounded-full shrink-0" style={{
                        backgroundColor: competitorBarColors[item.name]
                      }} data-id="element-426" />
                                <div className="w-16 text-sm font-medium text-gray-700" data-id="element-427">
                                  {item.name}
                                </div>
                                <div className="flex-1 h-7 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-428">
                                  <motion.div initial={{
                          width: 0
                        }} animate={{
                          width: `${(item.rating || 0) / 5 * 100}%`
                        }} transition={{
                          duration: 0.5,
                          delay: i * 0.08
                        }} className="h-full rounded-full" style={{
                          backgroundColor: competitorBarColors[item.name],
                          opacity: item.name === 'BMW' ? 1 : 0.6
                        }} data-id="element-429" />
                                </div>
                                <div className="flex items-center gap-1 w-16 text-right" data-id="element-430">
                                  <span className={`text-sm font-bold ${item.name === 'BMW' ? 'text-[#1C69D4]' : 'text-gray-600'}`} data-id="element-431">
                                    {item.rating?.toFixed(1)}
                                  </span>
                                  <Star size={11} className="text-yellow-400 fill-yellow-400" data-id="element-432" />
                                </div>
                                <div className="text-xs text-gray-400 w-20" data-id="element-433">
                                  {item.count} Häuser
                                </div>
                              </div>)}
                        </div>
                      </div>
                      <div data-id="element-434">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-435">
                          Einzelne Autohäuser im Umkreis
                        </div>
                        <div className="space-y-2" data-id="element-436">
                          {radiusStores.map((store, i) => <motion.div key={i} initial={{
                      opacity: 0,
                      x: 10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: i * 0.06
                    }} className={`flex items-center justify-between p-2.5 rounded-lg border ${store.type === 'BMW' ? 'border-[#1C69D4]/20 bg-[#1C69D4]/5' : 'border-gray-100 bg-gray-50'}`} data-id="element-437">
                              <div className="flex items-center gap-2 min-w-0" data-id="element-438">
                                <div className="w-2 h-2 rounded-full shrink-0" style={{
                          backgroundColor: competitorBarColors[store.type]
                        }} data-id="element-439" />
                                <span className={`text-sm truncate ${store.type === 'BMW' ? 'font-medium text-gray-900' : 'text-gray-600'}`} data-id="element-440">
                                  {store.name}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 shrink-0 ml-2" data-id="element-441">
                                <span className="text-xs text-gray-400" data-id="element-442">
                                  {store.distance}
                                </span>
                                <div className="flex items-center gap-1" data-id="element-443">
                                  <span className={`text-sm font-semibold ${store.type === 'BMW' ? 'text-[#1C69D4]' : 'text-gray-600'}`} data-id="element-444">
                                    {store.rating.toFixed(1)}
                                  </span>
                                  <Star size={11} className="text-yellow-400 fill-yellow-400" data-id="element-445" />
                                </div>
                              </div>
                            </motion.div>)}
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </motion.div>}
          </AnimatePresence>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-446">
        <Card data-id="element-447">
          <div className="p-6" data-id="element-448">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-449">
              Wettbewerbsvergleich
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-450">
              Kategorien-Analyse aus Google-Bewertungen
            </p>
            <div className="h-80" data-id="element-451">
              <ResponsiveContainer width="100%" height="100%" data-id="element-452">
                <RadarChart data={radarData} data-id="element-453">
                  <PolarGrid stroke="#e5e7eb" data-id="element-454" />
                  <PolarAngleAxis dataKey="category" tick={{
                  fontSize: 11,
                  fill: '#6b7280'
                }} data-id="element-455" />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{
                  fontSize: 10,
                  fill: '#9ca3af'
                }} data-id="element-456" />
                  <Radar name="BMW" dataKey="BMW" stroke="#1C69D4" fill="#1C69D4" fillOpacity={0.3} strokeWidth={2} data-id="element-457" />
                  <Radar name="Mercedes" dataKey="Mercedes" stroke="#222222" fill="#222222" fillOpacity={0.1} strokeWidth={2} data-id="element-458" />
                  <Radar name="Audi" dataKey="Audi" stroke="#BB0A21" fill="#BB0A21" fillOpacity={0.15} strokeWidth={2} data-id="element-459" />
                  <Legend wrapperStyle={{
                  fontSize: '12px'
                }} data-id="element-460" />
                  <Tooltip contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }} data-id="element-461" />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg" data-id="element-462">
              <div className="text-xs text-gray-500 mb-2" data-id="element-463">Stärken von BMW:</div>
              <div className="flex flex-wrap gap-2" data-id="element-464">
                <Badge variant="success" data-id="element-465">Fahrzeugqualität: #1</Badge>
                <Badge variant="success" data-id="element-466">Servicequalität: #1</Badge>
                <Badge variant="info" data-id="element-467">Beratung: #1</Badge>
              </div>
              <div className="text-xs text-gray-500 mt-3 mb-2" data-id="element-468">
                Verbesserungspotenzial:
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-469">
                <Badge variant="warning" data-id="element-470">Preis-Leistung: #3</Badge>
                <Badge variant="warning" data-id="element-471">Erreichbarkeit: #3</Badge>
              </div>
            </div>
          </div>
        </Card>

        <Card data-id="element-472">
          <div className="p-6" data-id="element-473">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-474">
              Kategorie-Details
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-475">
              Detaillierter Vergleich nach Bewertungskategorie
            </p>
            <div className="space-y-4" data-id="element-476">
              {radarData.map((item, index) => {
              const maxScore = Math.max(item.BMW, item.Mercedes, item.Audi);
              const isBMWLeading = item.BMW === maxScore;
              return <motion.div key={item.category} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className="space-y-2" data-id="element-477">
                    <div className="flex items-center justify-between" data-id="element-478">
                      <span className="text-sm font-medium text-gray-700" data-id="element-479">
                        {item.category}
                      </span>
                      {isBMWLeading && <Badge variant="success" className="text-xs" data-id="element-480">
                          Führend
                        </Badge>}
                    </div>
                    <div className="flex gap-2 h-6" data-id="element-481">
                      <div className="flex-1 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-482">
                        <div className="h-full rounded-full transition-all duration-500" style={{
                      width: `${item.BMW}%`,
                      backgroundColor: '#1C69D4'
                    }} data-id="element-483" />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-600" data-id="element-484">
                          {item.BMW}
                        </span>
                      </div>
                      <div className="w-16 text-xs text-gray-500 flex items-center" data-id="element-485">
                        BMW
                      </div>
                    </div>
                    <div className="flex gap-2 h-4" data-id="element-486">
                      <div className="flex-1 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-487">
                        <div className="h-full rounded-full opacity-70" style={{
                      width: `${item.Mercedes}%`,
                      backgroundColor: '#222222'
                    }} data-id="element-488" />
                      </div>
                      <div className="w-16 text-xs text-gray-400" data-id="element-489">
                        Merc. {item.Mercedes}
                      </div>
                    </div>
                    <div className="flex gap-2 h-4" data-id="element-490">
                      <div className="flex-1 bg-gray-100 rounded-full overflow-hidden relative" data-id="element-491">
                        <div className="h-full rounded-full opacity-80" style={{
                      width: `${item.Audi}%`,
                      backgroundColor: '#BB0A21'
                    }} data-id="element-492" />
                      </div>
                      <div className="w-16 text-xs text-gray-400" data-id="element-493">
                        Audi {item.Audi}
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </Card>
      </div>

      {/* Win/Loss Table */}
      <Card data-id="element-494">
        <div className="p-6" data-id="element-495">
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-496">
            Standort-Wettbewerb
          </h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-497">
            Wo gewinnen oder verlieren wir Kunden im 5km-Radius?
          </p>
          <div className="overflow-x-auto" data-id="element-498">
            <table className="w-full" data-id="element-499">
              <thead data-id="element-500">
                <tr className="border-b border-gray-200" data-id="element-501">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-502">
                    Standort
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-503">
                    BMW Autohaus
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-504">
                    Konkurrent
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-505">
                    Entfernung
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-506">
                    Ratings
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-507">
                    Kundenfluss
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-508">
                    Insight
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-509">
                {competitorAnalysis.map((item, index) => <motion.tr key={item.location} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className={`border-b border-gray-100 ${item.status === 'losing' ? 'bg-red-50/50' : item.status === 'winning' ? 'bg-green-50/50' : ''}`} data-id="element-510">
                    <td className="py-4 px-4" data-id="element-511">
                      <div className="flex items-center gap-2" data-id="element-512">
                        <MapPin size={14} className="text-gray-400" data-id="element-513" />
                        <span className="text-sm font-medium text-gray-900" data-id="element-514">
                          {item.location}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4" data-id="element-515">
                      <span className="text-sm text-gray-700" data-id="element-516">
                        {item.edeka}
                      </span>
                    </td>
                    <td className="py-4 px-4" data-id="element-517">
                      <div className="flex items-center gap-2" data-id="element-518">
                        <div className="w-2 h-2 rounded-full" style={{
                      backgroundColor: competitorColors[item.competitorType] || '#666'
                    }} data-id="element-519" />
                        <span className="text-sm text-gray-700" data-id="element-520">
                          {item.competitor}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center" data-id="element-521">
                      <span className="text-sm text-gray-500" data-id="element-522">
                        {item.distance}
                      </span>
                    </td>
                    <td className="py-4 px-4" data-id="element-523">
                      <div className="flex items-center justify-center gap-2" data-id="element-524">
                        <span className="text-sm font-semibold text-[#1C69D4]" data-id="element-525">
                          {item.edekaRating}
                        </span>
                        <ArrowRight size={14} className="text-gray-300" data-id="element-526" />
                        <span className="text-sm text-gray-500" data-id="element-527">
                          {item.competitorRating}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center" data-id="element-528">
                      <span className={`text-sm font-semibold ${item.status === 'winning' ? 'text-green-600' : item.status === 'losing' ? 'text-red-600' : 'text-gray-500'}`} data-id="element-529">
                        {item.customerFlow}
                      </span>
                    </td>
                    <td className="py-4 px-4" data-id="element-530">
                      <span className="text-xs text-gray-500" data-id="element-531">
                        {item.insight}
                      </span>
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}